import {
    calculatorItemLabel,
    calculatorFishingSourceTraces,
    isCalculatorPresentationIconUrl,
} from "./calculator-layer-inspector-contract.js";
import { calculatorFishingSourcesEqual } from "./calculator-fishing-source.js";
import {
    calculatorDataEnumLabel as enumLabel,
    calculatorDataLocale,
    calculatorDataText as t,
} from "./calculator-data-i18n.js";
import {
    createCalculatorItemPriceStateResolver,
    createCalculatorItemPriceTextResolver,
} from "./loot-sankey-projection.js";
import { createCalculatorProvenanceSegmentResolver } from "./calculator-provenance-projection.js";
import {
    DEFAULT_CALCULATOR_LOOT_SORT,
    calculatorLootEvidenceSortValue,
    calculatorLootRowSortValues,
    compareCalculatorLootRows,
    parseCalculatorLootSort,
} from "./calculator-loot-sort.js";
import { provenanceRailMarkup } from "./provenance-indicator.js";
import {
    lootItemRowMarkup,
    renderLootMetricCell,
} from "./loot-item-row.js";
import {
    CALCULATOR_LOOT_RATE_DISPLAY_MODES,
    calculatorLootEditableRateInputs,
    calculatorLootItemRateDisplayForMode,
    calculatorLootRateDisplayForMode,
    parseCalculatorLootRateDisplayMode,
} from "./calculator-loot-rate-presentation.js";
import { calculatorMetricTextWithUnit } from "./calculator-metric-unit-mark.js";
import { calculatorItemQuantityDisplay } from "./calculator-item-quantity-presentation.js";
import { parseCalculatorLootSubject } from "./calculator-loot-subject.js";
import {
    calculatorRateAlternativeFamiliesForGroup,
} from "./calculator-rate-alternative-presentation.js";
import { lootConditionSwitcherMarkup } from "./loot-condition-switcher.js";
import { isCalculatorGroupRoleV1 } from "../calculator-group-role-v1.js";
import {
    createCalculatorCatalogItemFactsEditorState,
    renderCalculatorCatalogItemFactsControls,
} from "./calculator-catalog-item-facts-editor.js";

function escapeText(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function presentedItemLabel(itemId, customCatalogItems, dataPresentation) {
    const label = dataPresentation?.itemsByItemId?.[String(itemId)]?.label;
    return typeof label === "string" && label.trim()
        ? label.trim()
        : calculatorItemLabel(itemId, customCatalogItems);
}

function presentedGroupLabel(groupId, dataPresentation) {
    const label = dataPresentation?.groupsByGroupId?.[groupId]?.label;
    return typeof label === "string" && label.trim() ? label.trim() : groupId;
}

function presentedMainGroupBadge(groupId, dataPresentation) {
    const mainGroup = dataPresentation?.groupsByGroupId?.[groupId]?.sourceMainGroup;
    if (
        !mainGroup
        || !["known", "unknown", "unavailable"].includes(mainGroup.state)
    ) {
        return "";
    }
    const knownId = mainGroup.state === "known"
        && Number.isSafeInteger(mainGroup.id)
        && mainGroup.id > 0
        ? mainGroup.id
        : null;
    const value = knownId === null ? enumLabel(mainGroup.state) : String(knownId);
    const idAttribute = knownId === null
        ? ""
        : ` data-loot-editor-main-group-id="${knownId}"`;
    return `<span class="badge badge-outline badge-sm shrink-0 whitespace-nowrap font-normal" data-loot-editor-main-group-state="${escapeText(mainGroup.state)}"${idAttribute}>${escapeText(t(
        "calculator.data.group.main_group_id",
        "Main group ID: { $value }",
        { value },
    ))}</span>`;
}

function selectedOption(value, expected) {
    return value === expected ? " selected" : "";
}

const EDITOR_RATE_BASES = Object.freeze([
    "ratio",
    "percent",
    "denormalizedWeight",
    "rawWeight",
]);
const QUANTIFIED_RATE_BASES = Object.freeze(["ratio", "percent"]);
const EDITOR_GRADE_TONES = new Set(["red", "yellow", "blue", "green", "white", "unknown"]);
function effectiveCandidate(trace) {
    return trace?.candidates?.find((candidate) => candidate.status === "effective") ?? null;
}

function effectiveItemPresences(traces) {
    const presences = new Map();
    for (const trace of traces) {
        if (trace.factKey.kind !== "itemPresence") {
            continue;
        }
        const candidate = effectiveCandidate(trace);
        const presence = candidate?.operation?.presence;
        if (presence !== "present" && presence !== "absent" && presence !== "unknown") {
            continue;
        }
        presences.set(trace.factKey.itemId, Object.freeze({
            state: presence,
            traceId: trace.traceId,
            candidate,
        }));
    }
    return presences;
}

function editorRate(value, path) {
    if (value === undefined) {
        return Object.freeze({ state: "unknown" });
    }
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be a typed rate`);
    }
    if (
        value.state === "known"
        && typeof value.value === "number"
        && Number.isFinite(value.value)
        && value.value >= 0
        && EDITOR_RATE_BASES.includes(value.basis)
    ) {
        return value;
    }
    if (value.state === "unknown") {
        return value;
    }
    if (
        value.state === "unavailable"
        && value.reason
        && typeof value.reason === "object"
        && typeof value.reason.code === "string"
    ) {
        return value;
    }
    throw new TypeError(`${path} is not a supported typed rate`);
}

export function parseCalculatorLootEditorQuantifiedRate(value, path = "$.rate") {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be a quantified rate`);
    }
    const fields = Object.keys(value).sort();
    if (fields.length !== 2 || fields[0] !== "basis" || fields[1] !== "value") {
        throw new TypeError(`${path} must contain exactly value and basis`);
    }
    if (
        typeof value.value !== "number"
        || !Number.isFinite(value.value)
        || value.value < 0
        || !QUANTIFIED_RATE_BASES.includes(value.basis)
    ) {
        throw new TypeError(`${path} is not a supported quantified rate`);
    }
    return Object.freeze({
        state: "known",
        value: value.value,
        basis: value.basis,
    });
}

function effectiveRateFact(traces, kind, groupId, itemId = null) {
    return effectiveRateFactWithOptions(traces, kind, groupId, itemId);
}

function hasEditableDirectConditions(operation) {
    const conditions = Array.isArray(operation?.conditions)
        ? operation.conditions
        : [];
    return conditions.length === 0;
}

function effectiveRateFactWithOptions(
    traces,
    kind,
    groupId,
    itemId = null,
) {
    const matching = traces.filter((candidateTrace) => (
        candidateTrace.factKey.kind === kind
        && candidateTrace.factKey.groupId === groupId
        && (kind !== "itemRate" || candidateTrace.factKey.itemId === itemId)
        && effectiveCandidate(candidateTrace)
    ));
    const trace = matching.find((candidateTrace) => (
        candidateTrace.factKey.branchId === null
        && hasEditableDirectConditions(effectiveCandidate(candidateTrace)?.operation)
    )) ?? null;
    const candidate = effectiveCandidate(trace);
    const operation = candidate?.operation ?? null;
    const conditions = Array.isArray(operation?.conditions)
        ? operation.conditions
        : [];
    return Object.freeze({
        traceId: trace?.traceId ?? null,
        candidate,
        rate: editorRate(operation?.rate, `${kind} ${itemId ?? groupId}`),
        derived: trace === null && matching.length > 0,
        preserveSourceContexts: trace === null
            ? matching.some((candidateTrace) => candidateTrace.factKey.branchId !== null)
            : conditions.length > 0,
    });
}

function editableGroupRateFact(traces, groupId) {
    const direct = effectiveRateFactWithOptions(traces, "groupRate", groupId);
    return Object.freeze({
        ...direct,
        editLabel: direct.candidate?.layer?.kind === "user"
            ? t(
                "calculator.data.rate_editor.user_group_override",
                "user group override",
            )
            : t("calculator.data.rate_editor.group_rate", "group rate input"),
    });
}

function projectedRawWeight(row, path) {
    if (!Object.prototype.hasOwnProperty.call(row, "rawWeight")) {
        throw new TypeError(`${path} must provide a typed raw weight`);
    }
    const rawWeight = editorRate(row.rawWeight, path);
    if (rawWeight.state === "known" && rawWeight.basis !== "rawWeight") {
        throw new TypeError(`${path} known values must use rawWeight basis`);
    }
    return rawWeight;
}

function projectedDenormalizedRate(row, path) {
    if (!Object.prototype.hasOwnProperty.call(row, "denormalizedRate")) {
        throw new TypeError(`${path} must provide a typed denormalized rate`);
    }
    const rate = editorRate(row.denormalizedRate, path);
    if (rate.state === "known" && rate.basis !== "percent") {
        throw new TypeError(`${path} known values must use percent basis`);
    }
    return rate;
}

function projectedGroupRow(projection, groupId) {
    const quantified = projection.sankey?.groups?.find((entry) => (
        entry.groupId === groupId
    ));
    const unquantified = projection.sankey?.unquantifiedGroups?.find((entry) => (
        entry.groupId === groupId
    ));
    return quantified ?? unquantified ?? null;
}

function projectedGroupRates(projection, groupId) {
    const row = projectedGroupRow(projection, groupId);
    if (!row) {
        const unknown = editorRate(undefined, `projection group rate ${groupId}`);
        return Object.freeze({
            normalizedRate: unknown,
            denormalizedRate: unknown,
            rawWeight: unknown,
        });
    }
    const normalizedRate = Object.prototype.hasOwnProperty.call(row.rate, "state")
        ? editorRate(row.rate, `projection group rate ${groupId}`)
        : parseCalculatorLootEditorQuantifiedRate(
            row.rate,
            `projection group rate ${groupId}`,
        );
    return Object.freeze({
        normalizedRate,
        denormalizedRate: projectedDenormalizedRate(
            row,
            `projection group denormalized rate ${groupId}`,
        ),
        rawWeight: projectedRawWeight(
            row,
            `projection group raw weight ${groupId}`,
        ),
    });
}

function projectedGroupRateAuthoring(projection, groupId, presence) {
    if (!Array.isArray(projection.groupRateAuthoring)) {
        throw new TypeError("projection must provide typed group rate authoring rows");
    }
    const matches = projection.groupRateAuthoring.filter((entry) => (
        entry?.groupId === groupId
    ));
    if (matches.length === 0 && presence === "absent") {
        const unknown = editorRate(undefined, `projection group rate authoring ${groupId}`);
        return Object.freeze({
            normalizedRate: unknown,
            denormalizedRate: unknown,
            rawWeight: unknown,
            shadowedBonuses: Object.freeze([]),
        });
    }
    if (matches.length !== 1) {
        throw new TypeError(
            `projection must provide exactly one group rate authoring row for ${groupId}`,
        );
    }
    const row = matches[0];
    const fields = [
        ["normalized", "percent"],
        ["denormalized", "denormalizedWeight"],
        ["rawWeight", "rawWeight"],
    ];
    const parsed = Object.fromEntries(fields.map(([field, basis]) => {
        const rate = editorRate(
            row[field],
            `projection group rate authoring ${groupId}.${field}`,
        );
        if (rate.state === "known" && rate.basis !== basis) {
            throw new TypeError(
                `projection group rate authoring ${groupId}.${field} must use ${basis} basis`,
            );
        }
        return [field, rate];
    }));
    if (!Array.isArray(row.shadowedBonuses)) {
        throw new TypeError(
            `projection group rate authoring ${groupId}.shadowedBonuses must be an array`,
        );
    }
    return Object.freeze({
        normalizedRate: parsed.normalized,
        denormalizedRate: parsed.denormalized,
        rawWeight: parsed.rawWeight,
        shadowedBonuses: Object.freeze([...row.shadowedBonuses]),
    });
}

function effectiveGroupPresenceFact(traces, groupId) {
    const matches = traces.filter((trace) => (
        trace.factKey.kind === "groupPresence"
        && trace.factKey.groupId === groupId
    ));
    if (matches.length === 0) {
        return null;
    }
    if (matches.length !== 1) {
        throw new TypeError(`projection must provide exactly one presence trace for ${groupId}`);
    }
    const candidate = effectiveCandidate(matches[0]);
    const operation = candidate?.operation;
    if (
        operation?.kind !== "groupPresence"
        || !["present", "absent", "unknown"].includes(operation.presence)
        || !isCalculatorGroupRoleV1(operation.role)
    ) {
        throw new TypeError(`projection group ${groupId} has no typed effective presence`);
    }
    return Object.freeze({
        traceId: matches[0].traceId,
        candidate,
        presence: operation.presence,
        role: operation.role,
    });
}

function projectedItemRow(projection, groupId, itemId) {
    const quantified = projection.sankey?.items?.find((entry) => (
        entry.groupId === groupId && entry.itemId === itemId
    ));
    const unquantified = projection.unquantifiedItems?.find((entry) => (
        entry.groupId === groupId && entry.itemId === itemId
    ));
    return quantified ?? unquantified ?? null;
}

function effectiveItemQuantityFact(traces, groupId, itemId) {
    const trace = traces.find((candidateTrace) => (
        candidateTrace.factKey.kind === "itemQuantity"
        && candidateTrace.factKey.groupId === groupId
        && candidateTrace.factKey.itemId === itemId
    )) ?? null;
    return Object.freeze({
        traceId: trace?.traceId ?? null,
        candidate: effectiveCandidate(trace),
    });
}

function effectiveItemIsFish(traces, itemId) {
    const trace = traces.find((candidateTrace) => (
        candidateTrace.factKey.kind === "itemIdentity"
        && candidateTrace.factKey.itemId === itemId
    )) ?? null;
    const isFish = effectiveCandidate(trace)?.operation?.isFish;
    return typeof isFish === "boolean" ? isFish : null;
}

function projectedItemRates(projection, groupId, itemId) {
    const row = projectedItemRow(projection, groupId, itemId);
    const quantified = row?.rate
        && !Object.prototype.hasOwnProperty.call(row.rate, "state")
        ? row
        : null;
    if (quantified) {
        return Object.freeze({
            normalizedRate: parseCalculatorLootEditorQuantifiedRate(
                quantified.rate,
                `projection item rate ${itemId}/${groupId}`,
            ),
            denormalizedRate: projectedDenormalizedRate(
                quantified,
                `projection item denormalized rate ${itemId}/${groupId}`,
            ),
            rawWeight: projectedRawWeight(
                quantified,
                `projection item raw weight ${itemId}/${groupId}`,
            ),
        });
    }
    if (!row) {
        const unknown = editorRate(
            undefined,
            `projection item rate ${itemId}/${groupId}`,
        );
        return Object.freeze({
            normalizedRate: unknown,
            denormalizedRate: unknown,
            rawWeight: unknown,
        });
    }
    return Object.freeze({
        normalizedRate: editorRate(
            row.rate,
            `projection item rate ${itemId}/${groupId}`,
        ),
        denormalizedRate: projectedDenormalizedRate(
            row,
            `projection item denormalized rate ${itemId}/${groupId}`,
        ),
        rawWeight: projectedRawWeight(
            row,
            `projection item raw weight ${itemId}/${groupId}`,
        ),
    });
}

function editableItemRateFact(traces, row, groupId, itemId, groupInactive) {
    const appliedTraceIds = !groupInactive
        && row
        && Array.isArray(row.traceIds)
        ? new Set(row.traceIds)
        : null;
    const appliedTraces = appliedTraceIds === null
        ? traces
        : traces.filter(({ traceId }) => appliedTraceIds.has(traceId));
    const direct = effectiveRateFact(appliedTraces, "itemRate", groupId, itemId);
    const editLabel = t(
        "calculator.data.rate_editor.item_base",
        "item base input",
    );
    if (
        !direct.derived
        || !row
        || !Object.prototype.hasOwnProperty.call(row, "rawWeight")
        || !Array.isArray(row.traceIds)
    ) {
        return Object.freeze({ ...direct, editLabel });
    }
    const rawWeight = projectedRawWeight(
        row,
        `projection item raw weight ${itemId}/${groupId}`,
    );
    if (rawWeight.state !== "known") {
        return Object.freeze({ ...direct, editLabel });
    }
    const contributing = appliedTraces
        .filter((trace) => (
            trace.factKey.kind === "itemRate"
            && trace.factKey.groupId === groupId
            && trace.factKey.itemId === itemId
            && effectiveCandidate(trace)
        ))
        .sort((left, right) => left.traceId.localeCompare(right.traceId));
    if (!contributing.length || contributing.some((trace) => {
        const rate = effectiveCandidate(trace)?.operation?.rate;
        return rate?.state !== "known"
            || rate.basis !== "rawWeight"
            || rate.value !== rawWeight.value;
    })) {
        return Object.freeze({ ...direct, editLabel });
    }
    return Object.freeze({
        traceId: contributing[0].traceId,
        candidate: effectiveCandidate(contributing[0]),
        rate: rawWeight,
        derived: false,
        preserveSourceContexts: true,
        editLabel,
    });
}

function effectiveMemberships(projection, traces, presences) {
    return traces.flatMap((trace) => {
        if (trace.factKey.kind !== "groupMembership") {
            return [];
        }
        const candidate = effectiveCandidate(trace);
        if (candidate?.operation?.membership !== "member") {
            return [];
        }
        const presence = presences.get(trace.factKey.itemId) ?? null;
        const row = projectedItemRow(
            projection,
            trace.factKey.groupId,
            trace.factKey.itemId,
        );
        const groupPresence = effectiveGroupPresenceFact(
            traces,
            trace.factKey.groupId,
        )?.presence
            ?? projectedGroupRow(projection, trace.factKey.groupId)?.presence
            ?? "unknown";
        const rateFact = editableItemRateFact(
            traces,
            row,
            trace.factKey.groupId,
            trace.factKey.itemId,
            groupPresence === "absent",
        );
        const quantityFact = effectiveItemQuantityFact(
            traces,
            trace.factKey.groupId,
            trace.factKey.itemId,
        );
        const presenceTraceIds = traces
            .filter((candidateTrace) => (
                candidateTrace.factKey.kind === "itemPresence"
                && candidateTrace.factKey.itemId === trace.factKey.itemId
            ))
            .map(({ traceId }) => traceId);
        const inspectorOnlyQuantityTraceId = row === null && quantityFact.candidate
            ? quantityFact.traceId
            : null;
        return [Object.freeze({
            itemId: trace.factKey.itemId,
            groupId: trace.factKey.groupId,
            traceId: trace.traceId,
            presence,
            presenceTraceId: presence?.traceId ?? null,
            candidate,
            projectionTraceIds: uniqueTraceIds(
                row?.traceIds ?? [],
                trace.traceId,
                presenceTraceIds,
                rateFact.traceId,
                inspectorOnlyQuantityTraceId,
            ),
            rateFact,
            quantityFact,
            quantity: row?.quantity ?? null,
            expectedQuantity: row?.expectedQuantity ?? null,
            isFish: effectiveItemIsFish(traces, trace.factKey.itemId),
            projectedRates: row
                ? projectedItemRates(
                    projection,
                    trace.factKey.groupId,
                    trace.factKey.itemId,
                )
                : Object.freeze({
                    normalizedRate: rateFact.rate,
                    denormalizedRate: editorRate(
                        undefined,
                        `projection item denormalized rate ${trace.factKey.itemId}/${trace.factKey.groupId}`,
                    ),
                    rawWeight: rateFact.rate,
                }),
        })];
    });
}

const FALLBACK_ITEM_STYLE = Object.freeze({
    iconGradeTone: "unknown",
    gradeRank: 0,
    fillColor: "var(--color-base-200)",
    strokeColor: "var(--color-base-300)",
    textColor: "var(--color-base-content)",
    connectorColor: "var(--color-primary)",
});

function spriteIcon(name, className = "size-4") {
    return `<svg class="fishy-icon ${escapeText(className)}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="/img/icons.svg#fishy-${escapeText(name)}"></use></svg>`;
}

function renderShadowedGroupBonusAlert(rate, shadowedBonuses, groupId) {
    const hasFixedNormalizedRate = rate?.state === "known"
        && (rate.basis === "percent" || rate.basis === "ratio");
    if (!hasFixedNormalizedRate || !shadowedBonuses.length) {
        return "";
    }
    const message = t(
        "calculator.data.rate_editor.shadowed_bonus",
        "This fixed normalized rate does not apply its raw-weight bonuses; edit the rate in Denormalized or Raw Weight to apply them.",
    );
    return `<span class="z-10 inline-flex shrink-0 text-warning" role="note" tabindex="0" data-fishy-tooltip="${escapeText(message)}" data-loot-editor-shadowed-bonus data-group-id="${escapeText(groupId)}">`
        + spriteIcon("alert-fill", "size-4")
        + `<span class="sr-only">${escapeText(message)}</span></span>`;
}

function lootItemIdentity(itemId, customCatalogItems, dataPresentation, priceTextResolver) {
    const presentation = dataPresentation?.itemsByItemId?.[String(itemId)] ?? null;
    const fallbackStyle = dataPresentation?.unknownItem ?? FALLBACK_ITEM_STYLE;
    const style = presentation ?? fallbackStyle;
    const label = presentedItemLabel(itemId, customCatalogItems, dataPresentation);
    return Object.freeze({
        itemId,
        label,
        priceText: priceTextResolver?.resolve(
            itemId,
            `lootEditor.items.${itemId}.price`,
        ) ?? null,
        iconUrl: isCalculatorPresentationIconUrl(presentation?.iconUrl)
            ? presentation.iconUrl
            : null,
        iconGradeTone: EDITOR_GRADE_TONES.has(style?.iconGradeTone)
            ? style.iconGradeTone
            : "unknown",
        fillColor: style?.fillColor ?? FALLBACK_ITEM_STYLE.fillColor,
        strokeColor: style?.strokeColor ?? FALLBACK_ITEM_STYLE.strokeColor,
        textColor: style?.textColor ?? FALLBACK_ITEM_STYLE.textColor,
        connectorColor: style?.connectorColor ?? FALLBACK_ITEM_STYLE.connectorColor,
    });
}

function uniqueTraceIds(...traceIds) {
    return Object.freeze([...new Set(traceIds.flat().filter((traceId) => (
        typeof traceId === "string" && traceId
    )))]);
}

function editorProvenanceRail(
    resolver,
    traceIds,
    path,
    dataPresentation,
    provenanceDetailBindings,
) {
    if (!resolver || traceIds.length === 0) {
        return "";
    }
    const segments = traceIds.map((traceId, index) => resolver.resolveEffective(
        traceId,
        `${path}.traces[${index}]`,
    ));
    if (!segments.length) {
        return "";
    }
    return provenanceRailMarkup(segments, {
        label: dataPresentation?.labels?.provenanceLabel
            ?? t("common.provenance.label.default", "Provenance"),
        detailBindings: provenanceDetailBindings,
    });
}

function editorPresenceProvenanceSegments(
    resolver,
    effectiveTraceId,
    traceIds,
    itemId,
    path,
) {
    if (!resolver || traceIds.length === 0) {
        return [];
    }
    let segment = effectiveTraceId
        ? resolver.resolveCatalogItemPresence(
            effectiveTraceId,
            traceIds,
            itemId,
            path,
        )
        : null;
    if (segment === null) {
        const effectiveIndex = traceIds.findIndex((traceId, index) => (
            resolver.resolve(
                [traceId],
                `${path}.traces[${index}]`,
            ).some(({ active }) => active)
        ));
        segment = effectiveIndex < 0
            ? null
            : resolver.resolveEffective(
                traceIds[effectiveIndex],
                `${path}.traces[${effectiveIndex}]`,
            );
    }
    if (!segment) {
        return [];
    }
    return [segment];
}

function editorSemanticItemProvenanceSegments(
    resolver,
    traceIds,
    itemId,
    groupId,
    path,
) {
    if (!resolver) {
        return [];
    }
    return resolver.resolveInspectorItem(
        traceIds,
        itemId,
        groupId,
        path,
    );
}

function editorSemanticGroupProvenanceRail(
    resolver,
    traceIds,
    groupId,
    groupRole,
    path,
    dataPresentation,
    provenanceDetailBindings,
) {
    if (!resolver) {
        return "";
    }
    const { segments } = resolver.resolveSemanticGroup(
        traceIds,
        groupId,
        groupRole,
        path,
    );
    return provenanceRailMarkup(segments, {
        label: dataPresentation?.labels?.provenanceLabel
            ?? t("common.provenance.label.default", "Provenance"),
        detailBindings: provenanceDetailBindings,
    });
}

function lootRateDisplay(rates, mode, locale, { itemRate = false } = {}) {
    const display = itemRate
        ? calculatorLootItemRateDisplayForMode
        : calculatorLootRateDisplayForMode;
    return display(
        rates.normalizedRate,
        rates.denormalizedRate,
        rates.rawWeight,
        mode,
        {
            locale,
            numberFormat: {
                minimumFractionDigits: 0,
                maximumFractionDigits: 6,
            },
            unknownText: t("calculator.data.rate.unknown", "Unknown rate"),
            unavailableText: t("calculator.data.rate.unavailable", "Unavailable rate"),
        },
    );
}

function lootRateAccessibleText(display) {
    if (display.state !== "known") {
        return display.text;
    }
    const text = display.titleText ?? display.text;
    if (display.basis === "rawWeight") {
        return `${text} ${enumLabel("rawWeight")}`;
    }
    if (display.basis === "denormalizedWeight") {
        return `${text} ${t(
            "calculator.server.tab.denormalized",
            "Denormalized",
        )}`;
    }
    return `${text}${display.titleText ? "" : "%"} ${t(
        "calculator.data.rate_basis.normalized_percent",
        "Normalized",
    )}`;
}

function lootMetricValue(display, { compactNonKnown = false } = {}) {
    return Object.freeze({
        text: calculatorMetricTextWithUnit(display),
        unitMark: display.unitMark,
        ...(compactNonKnown ? { state: display.state } : {}),
    });
}

function lootMetricTone(style) {
    return Object.freeze({
        fillColor: style?.fillColor ?? FALLBACK_ITEM_STYLE.fillColor,
        strokeColor: style?.strokeColor ?? FALLBACK_ITEM_STYLE.strokeColor,
        textColor: style?.textColor ?? FALLBACK_ITEM_STYLE.textColor,
    });
}

function lootItemPresentation(
    identity,
    control,
    dataPresentation,
    quantityDisplay = null,
) {
    return Object.freeze({
        label: identity.label,
        iconUrl: identity.iconUrl,
        iconGradeTone: identity.iconGradeTone,
        priceText: identity.priceText,
        priceLabel: dataPresentation?.labels?.basePriceLabel
            ?? t("calculator.server.loot.breakdown.label.base_price", "Base price"),
        quantityText: quantityDisplay?.text ?? null,
        quantityLabel: quantityDisplay?.ariaLabel ?? null,
        control,
    });
}

function renderLootQuantityForm(assignment, modelLoading) {
    const quantity = assignment.quantity?.state === "known"
        ? assignment.quantity
        : null;
    const operation = assignment.quantityFact.candidate?.operation;
    const userOperationId = assignment.quantityFact.candidate?.layer?.kind === "user"
        && typeof operation?.operationId === "string"
        ? operation.operationId
        : null;
    return `<fieldset class="fieldset rounded-box border border-base-300 bg-base-100/70 p-2" data-loot-editor-quantity-form data-group-id="${escapeText(assignment.groupId)}" data-item-id="${assignment.itemId}"${modelLoading ? " disabled" : ""}>`
        + `<legend class="fieldset-legend text-xs font-semibold">${escapeText(t("calculator.data.quantity.editor_title", "Drop quantity"))}</legend>`
        + '<div class="flex flex-wrap items-end gap-2">'
        + `<label class="fieldset-label grid gap-1 text-xs"><span>${escapeText(t("calculator.data.quantity.minimum", "Minimum"))}</span><input class="input input-bordered input-xs w-24" type="number" min="0" max="4294967295" step="1" inputmode="numeric" value="${quantity ? quantity.minimum : ""}" data-loot-editor-quantity-minimum></label>`
        + `<label class="fieldset-label grid gap-1 text-xs"><span>${escapeText(t("calculator.data.quantity.maximum", "Maximum"))}</span><input class="input input-bordered input-xs w-24" type="number" min="0" max="4294967295" step="1" inputmode="numeric" value="${quantity ? quantity.maximum : ""}" data-loot-editor-quantity-maximum></label>`
        + (userOperationId
            ? `<button class="btn btn-ghost btn-xs" type="button" data-action="revert-user-operation" data-operation-id="${escapeText(userOperationId)}">${escapeText(t("calculator.data.action.revert_quantity", "Revert"))}</button>`
            : "")
        + '</div></fieldset>';
}

function rateEditorMatches(rateEditor, scope, groupId, itemId = null) {
    return rateEditor?.scope === scope
        && rateEditor.groupId === groupId
        && (scope !== "item" || rateEditor.itemId === itemId);
}

function editableRateInputs(rate, preferredBasis) {
    return calculatorLootEditableRateInputs(rate, {
        preferredBasis: preferredBasis ?? null,
    });
}

function rateBasisTab({ basis, description, descriptionId, label, icon, name, input }) {
    const active = input.basis === basis;
    return `<label role="tab" class="tab gap-1.5 whitespace-nowrap${active ? " tab-active" : ""}" aria-selected="${active}" data-fishy-tooltip="${escapeText(description)}" data-loot-editor-rate-basis-option>`
        + `<input class="sr-only" type="radio" name="${escapeText(name)}" value="${basis}" data-loot-editor-rate-basis data-rate-basis="${basis}" data-rate-seed="${escapeText(input.inputs[basis].value)}" aria-label="${escapeText(label)}" aria-describedby="${escapeText(descriptionId)}"${active ? " checked" : ""}>`
        + spriteIcon(icon, "fishy-loot-rate-icon size-3.5")
        + `<span>${escapeText(label)}</span><span id="${escapeText(descriptionId)}" class="sr-only">${escapeText(description)}</span></label>`;
}

function renderLootRateProjections({
    projectedRates,
    activeMode,
    locale,
    tone,
    itemRate,
}) {
    const inactiveModes = CALCULATOR_LOOT_RATE_DISPLAY_MODES.filter((mode) => (
        mode !== activeMode
    ));
    return '<div class="order-first flex max-w-full gap-1 overflow-x-auto opacity-70" data-loot-editor-rate-projections>'
        + inactiveModes.map((mode) => {
            const display = lootRateDisplay(
                projectedRates,
                mode,
                locale,
                { itemRate },
            );
            return `<div class="shrink-0" data-loot-editor-rate-projection="${escapeText(mode)}">`
                + renderLootMetricCell({
                    primary: lootMetricValue(display, { compactNonKnown: true }),
                    secondary: null,
                    tone,
                    control: {
                        type: "static",
                        ariaLabel: lootRateAccessibleText(display),
                    },
                })
                + "</div>";
        }).join("")
        + "</div>";
}

function renderLootRateForm({
    scope,
    groupId,
    itemId = null,
    rateFact,
    projectedRates = null,
    projectionDisplayMode = "normalized",
    projectionLocale = "en-US",
    projectionTone = lootMetricTone(FALLBACK_ITEM_STYLE),
    preferredBasis = null,
    modelLoading = false,
}) {
    const rate = rateFact.rate;
    const input = editableRateInputs(rate, preferredBasis);
    const label = rateFact.editLabel
        ?? (scope === "group"
            ? t("calculator.data.rate_editor.group_rate", "group rate input")
            : t("calculator.data.rate_editor.item_base", "item base input"));
    const basisName = `loot-rate-basis-${scope}-${groupId}-${itemId ?? "group"}`;
    const normalizedLabel = t("calculator.server.tab.normalized", "Normalized");
    const denormalizedLabel = t("calculator.server.tab.denormalized", "Denormalized");
    const rawWeightLabel = enumLabel("rawWeight");
    const normalizedDescription = t(
        "common.rate_basis.normalized.description",
        "Final allocated percentage after fixed shares and weights are resolved.",
    );
    const denormalizedDescription = t(
        "common.rate_basis.denormalized.description",
        "The same raw weight divided by 10,000 and displayed with %.",
    );
    const rawWeightDescription = t(
        "common.rate_basis.raw_weight.description",
        "A non-negative relative input used to divide the probability left after fixed shares.",
    );
    const projections = renderLootRateProjections({
        projectedRates,
        activeMode: projectionDisplayMode,
        locale: projectionLocale,
        tone: projectionTone,
        itemRate: scope === "item",
    });
    return `<fieldset class="fieldset" data-loot-editor-rate-form data-rate-scope="${scope}" data-group-id="${escapeText(groupId)}"${itemId === null ? "" : ` data-item-id="${itemId}"`}${rateFact.preserveSourceContexts ? " data-rate-preserve-source-contexts" : ""}${modelLoading ? " disabled" : ""}>`
        + `<legend class="fieldset-legend text-xs font-semibold">${escapeText(t("calculator.data.rate_editor.edit", "Edit { $label }", { label }))}</legend>`
        + projections
        + '<div class="grid gap-2" data-loot-editor-rate-controls>'
        + `<div role="tablist" class="tabs tabs-box w-fit max-w-full overflow-x-auto" aria-label="${escapeText(t("calculator.data.field.rate_basis", "Input basis"))}">`
        + rateBasisTab({
            basis: "percent",
            description: normalizedDescription,
            descriptionId: `${basisName}-percent-description`,
            label: normalizedLabel,
            icon: "percentage-fill",
            name: basisName,
            input,
        })
        + rateBasisTab({
            basis: "denormalizedWeight",
            description: denormalizedDescription,
            descriptionId: `${basisName}-denormalized-description`,
            label: denormalizedLabel,
            icon: "weight",
            name: basisName,
            input,
        })
        + rateBasisTab({
            basis: "rawWeight",
            description: rawWeightDescription,
            descriptionId: `${basisName}-raw-weight-description`,
            label: rawWeightLabel,
            icon: "weight",
            name: basisName,
            input,
        })
        + '</div>'
        + `<input class="input input-bordered input-xs w-full" name="rateValue" type="text" inputmode="decimal" autocomplete="off" spellcheck="false" value="${escapeText(input.value)}" data-loot-editor-rate-value aria-label="${escapeText(t("calculator.data.field.rate_value_accessible", "Rate value. Normalized fixed rates must be between 0 and 100 percent. Denormalized and raw weights preserve the full projected decimal precision."))}">`
        + '</div></fieldset>';
}

function renderLootGroupItem(
    assignment,
    customCatalogItems,
    dataPresentation,
    provenanceResolver,
    priceTextResolver,
    selectedItemId,
    selectedLootSubject,
    rateEditor,
    modelLoading,
    lootRateDisplayMode,
    provenanceDetailBindings,
) {
    const identity = lootItemIdentity(
        assignment.itemId,
        customCatalogItems,
        dataPresentation,
        priceTextResolver,
    );
    const editing = rateEditorMatches(
        rateEditor,
        "item",
        assignment.groupId,
        assignment.itemId,
    );
    const canRenderRateForm = editing
        && (!assignment.rateFact.derived || rateEditor?.createOverride === true);
    const rate = assignment.projectedRates.normalizedRate;
    const locale = calculatorDataLocale(dataPresentation?.locale);
    const displayedRate = lootRateDisplay(
        assignment.projectedRates,
        lootRateDisplayMode,
        locale,
        { itemRate: true },
    );
    const accessibleRate = lootRateAccessibleText(displayedRate);
    const accessibleRateLabel = t(
        "calculator.data.rate.projected_edit_base",
        "Projected { $rate }; edit { $label }",
        {
            rate: accessibleRate,
            label: assignment.rateFact.editLabel
                ?? t("calculator.data.rate_editor.item_base", "item base input"),
        },
    );
    const rateAction = assignment.rateFact.derived
        ? "create-item-rate-override"
        : "edit-item-rate";
    const provenanceSegments = editorSemanticItemProvenanceSegments(
        provenanceResolver,
        assignment.projectionTraceIds,
        assignment.itemId,
        assignment.groupId,
        `lootEditor.groups.${assignment.groupId}.items.${assignment.itemId}`,
    );
    const removeLabel = t(
        "calculator.data.action.remove_item_from_group",
        "Remove { $item } from { $group }",
        {
            item: identity.label,
            group: presentedGroupLabel(assignment.groupId, dataPresentation),
        },
    );
    const inspectLabel = t(
        "calculator.data.action.inspect_item_assignment",
        "Inspect { $item } assignment in { $group }",
        {
            item: identity.label,
            group: presentedGroupLabel(assignment.groupId, dataPresentation),
        },
    );
    const selected = assignment.itemId === selectedItemId;
    const provenanceSelected = selectedLootSubject?.kind === "row"
        && selectedLootSubject.itemId === assignment.itemId
        && selectedLootSubject.groupId === assignment.groupId;
    const quantityProvenanceRelevant = provenanceSegments.some((segment) => (
        segment.quantityProvenanceRelevant === true
    ));
    const quantityDisplay = !quantityProvenanceRelevant
        || assignment.quantity === null
        || assignment.expectedQuantity === null
        ? null
        : calculatorItemQuantityDisplay(
            assignment.quantity,
            assignment.expectedQuantity,
            {
                locale,
                path: `lootEditor.groups.${assignment.groupId}.items.${assignment.itemId}`,
            },
        );
    const connectionAttributes = selected
        ? ` data-loot-editor-connection-target data-loot-editor-connection-label="${escapeText(removeLabel)}"`
        : "";
    const row = lootItemRowMarkup({
        rowId: `editor:${assignment.groupId}:${assignment.itemId}`,
        item: lootItemPresentation(identity, {
            type: "button",
            action: "select-loot-row",
            ariaLabel: inspectLabel,
            itemId: assignment.itemId,
            groupId: assignment.groupId,
            traceId: assignment.traceId,
            pressed: provenanceSelected,
        }, dataPresentation, quantityDisplay),
        leadingMetric: {
            primary: lootMetricValue(displayedRate, { compactNonKnown: true }),
            secondary: null,
            tone: lootMetricTone(identity),
            control: {
                type: "button",
                action: rateAction,
                ariaLabel: accessibleRateLabel,
                itemId: assignment.itemId,
                groupId: assignment.groupId,
                traceId: assignment.traceId,
                expanded: editing,
                disabled: modelLoading,
            },
        },
        trailingMetric: null,
        provenanceSegments,
        provenanceLabel: dataPresentation?.labels?.provenanceLabel
            ?? t("common.provenance.label.default", "Provenance"),
    }, {
        provenanceDetailBindings,
    });
    const content = row
        + (canRenderRateForm
            ? renderLootRateForm({
                scope: "item",
                groupId: assignment.groupId,
                itemId: assignment.itemId,
                rateFact: assignment.rateFact,
                projectedRates: assignment.projectedRates,
                projectionDisplayMode: lootRateDisplayMode,
                projectionLocale: locale,
                projectionTone: lootMetricTone(identity),
                preferredBasis: rateEditor?.inputBasis,
                modelLoading,
            })
            : "")
        + (provenanceSelected && assignment.isFish === false
            ? renderLootQuantityForm(assignment, modelLoading)
            : "");
    const listItem = `<li data-loot-editor-group-item="${assignment.itemId}" data-item-id="${assignment.itemId}" data-group-id="${escapeText(assignment.groupId)}" data-selected="${selected}" data-loot-provenance-selected="${provenanceSelected}" data-presence-state="${escapeText(assignment.presence?.state ?? "unknown")}" data-rate-state="${escapeText(rate.state)}"${connectionAttributes}>`;
    return listItem + content + '</li>';
}

function renderLootGroupContent({
    groupId,
    assignments,
    selectedItemId,
    selectedLootSubject,
    customCatalogItems,
    dataPresentation,
    provenanceResolver,
    priceTextResolver,
    projection,
    traces,
    rateEditor,
    modelLoading,
    lootRateDisplayMode,
    provenanceDetailBindings,
    rateAlternativeFamilies,
}) {
    const label = presentedGroupLabel(groupId, dataPresentation);
    const mainGroupBadge = presentedMainGroupBadge(groupId, dataPresentation);
    const provenanceSelected = selectedLootSubject?.kind === "group"
        && selectedLootSubject.groupId === groupId;
    const groupRate = editableGroupRateFact(traces, groupId);
    const projectedGroup = projectedGroupRow(projection, groupId);
    const groupPresenceFact = effectiveGroupPresenceFact(traces, groupId);
    const groupPresence = groupPresenceFact ?? Object.freeze({
        traceId: null,
        candidate: null,
        presence: projectedGroup?.presence ?? "unknown",
        role: projectedGroup?.role ?? "other",
    });
    const displayedGroupRates = projectedGroupRates(projection, groupId);
    const authoringGroupRates = projectedGroupRateAuthoring(
        projection,
        groupId,
        groupPresence.presence,
    );
    const editingGroupRate = rateEditorMatches(rateEditor, "group", groupId);
    const canRenderGroupRateForm = editingGroupRate
        && (!groupRate.derived || rateEditor?.createOverride === true);
    const locale = calculatorDataLocale(dataPresentation?.locale);
    const displayedRate = lootRateDisplay(
        displayedGroupRates,
        lootRateDisplayMode,
        locale,
    );
    const accessibleGroupRate = lootRateAccessibleText(displayedRate);
    const accessibleGroupRateLabel = t(
        "calculator.data.rate.projected_total_edit_base",
        "Projected total { $rate }; edit { $label }",
        {
            rate: accessibleGroupRate,
            label: groupRate.editLabel
                ?? t("calculator.data.rate_editor.group_rate", "group rate input"),
        },
    );
    const groupPresentation = dataPresentation?.groupsByGroupId?.[groupId]
        ?? FALLBACK_ITEM_STYLE;
    const groupRateAction = groupRate.derived
        ? "create-group-rate-override"
        : "edit-group-rate";
    const groupPresenceTraceIds = traces
        .filter((trace) => (
            trace.factKey.kind === "groupPresence"
            && trace.factKey.groupId === groupId
            && effectiveCandidate(trace)
        ))
        .map(({ traceId }) => traceId);
    const groupProvenanceTraceIds = uniqueTraceIds(
        projectedGroup?.traceIds ?? [],
        groupPresenceTraceIds,
        groupRate.traceId,
    );
    const groupProvenance = groupPresence.role === "prize"
        ? editorSemanticGroupProvenanceRail(
            provenanceResolver,
            groupProvenanceTraceIds,
            groupId,
            groupPresence.role,
            `lootEditor.groups.${groupId}`,
            dataPresentation,
            provenanceDetailBindings,
        )
        : editorProvenanceRail(
            provenanceResolver,
            uniqueTraceIds(groupRate.traceId),
            `lootEditor.groups.${groupId}.rate`,
            dataPresentation,
            provenanceDetailBindings,
        );
    const alreadyAssigned = assignments.some((entry) => entry.itemId === selectedItemId);
    const addDisabled = modelLoading || !Number.isInteger(selectedItemId) || alreadyAssigned;
    const addLabel = !Number.isInteger(selectedItemId)
        ? t("calculator.data.action.select_item_for_group", "Select an item to add to { $group }", { group: label })
        : alreadyAssigned
            ? t("calculator.data.action.item_already_in_group", "{ $item } is already in { $group }", {
                item: presentedItemLabel(selectedItemId, customCatalogItems, dataPresentation),
                group: label,
            })
            : t("calculator.data.action.add_item_to_group", "Add { $item } to { $group }", {
                item: presentedItemLabel(selectedItemId, customCatalogItems, dataPresentation),
                group: label,
            });
    const renderAssignment = (assignment) => renderLootGroupItem(
        assignment,
        customCatalogItems,
        dataPresentation,
        provenanceResolver,
        priceTextResolver,
        selectedItemId,
        selectedLootSubject,
        rateEditor,
        modelLoading,
        lootRateDisplayMode,
        provenanceDetailBindings,
    );
    const absentAssignments = assignments.filter((assignment) => (
        assignment.presence?.state === "absent"
    ));
    const activeAssignments = assignments.filter((assignment) => (
        assignment.presence?.state !== "absent"
    ));
    const activeRows = activeAssignments.map(renderAssignment).join("");
    const absentExpanded = absentAssignments.some((assignment) => (
        assignment.itemId === selectedItemId
        || (
            selectedLootSubject?.kind === "row"
            && selectedLootSubject.itemId === assignment.itemId
            && selectedLootSubject.groupId === assignment.groupId
        )
        || rateEditorMatches(
            rateEditor,
            "item",
            assignment.groupId,
            assignment.itemId,
        )
    ));
    const absentCount = t(
        absentAssignments.length === 1
            ? "calculator.data.available.item_count.one"
            : "calculator.data.available.item_count.other",
        absentAssignments.length === 1 ? "{ $count } item" : "{ $count } items",
        { count: absentAssignments.length },
    );
    const absentPresenceLabel = t(
        "calculator.data.presence.label",
        "Presence: { $state }",
        { state: enumLabel("absent") },
    );
    const absentRows = absentAssignments.length
        ? '<li class="min-w-0" data-loot-editor-absent-assignments>'
            + `<details class="collapse collapse-arrow isolation-auto border border-error/30 bg-error/5" data-loot-editor-absent-disclosure${absentExpanded ? " open" : ""}>`
            + '<summary class="collapse-title min-h-0 py-2 text-sm font-semibold" data-loot-editor-absent-summary>'
            + '<span class="flex min-w-0 items-center gap-2">'
            + `<span class="badge badge-soft badge-error badge-sm shrink-0 whitespace-nowrap">${escapeText(absentPresenceLabel)}</span>`
            + `<span class="badge badge-ghost badge-sm shrink-0 whitespace-nowrap">${escapeText(absentCount)}</span>`
            + '</span></summary>'
            + '<div class="collapse-content px-2 pb-2">'
            + `<ol class="grid gap-2" data-loot-editor-absent-list>${absentAssignments.map(renderAssignment).join("")}</ol>`
            + '</div></details></li>'
        : "";
    const list = assignments.length
        ? activeRows + absentRows
        : `<li class="text-xs opacity-60">${escapeText(t("calculator.data.group.no_memberships", "No effective item memberships."))}</li>`;
    const assignmentControl = Number.isInteger(selectedItemId)
        ? `<button class="btn btn-circle btn-xs ${alreadyAssigned ? "btn-success" : "btn-primary"}" type="button" aria-label="${escapeText(addLabel)}" data-fishy-tooltip="${escapeText(addLabel)}" data-fishy-tooltip-mode="anchor-only" aria-pressed="${alreadyAssigned}" data-loot-editor-group-assign data-action="add-selected-item-to-group" data-group-id="${escapeText(groupId)}"${addDisabled ? " disabled" : ""}>${spriteIcon("arrows-right-fill")}</button>`
        : "";
    const inspectGroupLabel = t(
        "calculator.data.action.inspect_group",
        "Inspect fishing group { $group }",
        { group: label },
    );
    const groupSelectClasses = provenanceSelected
        ? "btn-primary btn-active"
        : "btn-outline btn-primary";
    const groupSelectIcon = provenanceSelected
        ? "check-badge-solid"
        : "inspect-fill";
    const groupIsActive = groupPresence.presence === "present";
    const groupPresenceAction = groupIsActive
        ? "deactivate-loot-group"
        : "activate-loot-group";
    const groupPresenceLabel = groupIsActive
        ? t("calculator.data.action.deactivate_group", "Deactivate group")
        : t("calculator.data.action.activate_group", "Activate group");
    const groupPresenceAriaLabel = groupIsActive
        ? t(
            "calculator.data.action.deactivate_group_label",
            "Deactivate { $group } without deleting its entries",
            { group: label },
        )
        : t(
            "calculator.data.action.activate_group_label",
            "Activate { $group } with its saved entries",
            { group: label },
        );
    const conditionSwitchers = rateAlternativeFamilies.map((family) => (
        `<div class="fishy-loot-condition-family" data-calculator-rate-alternative-family="${escapeText(family.familyId)}" data-resolution-kind="${escapeText(family.resolutionKind)}">`
        + lootConditionSwitcherMarkup({
            selectionKey: family.familyId,
            selectedIndex: family.selectedIndex,
            options: family.options.map(({ id, label, title }) => ({ id, label, title })),
            previousLabel: t(
                "info.zone_loot.condition.previous",
                "Previous condition",
            ),
            nextLabel: t("info.zone_loot.condition.next", "Next condition"),
            unselectedLabel: family.resolutionKind === "inactive"
                ? t(
                    "calculator.data.condition.none_active",
                    "No active condition",
                )
                : t(
                    "calculator.data.unavailable.model_selection",
                    "Choose condition",
                ),
            disabled: modelLoading,
        })
        + (family.resolutionKind === "override"
            ? `<button class="btn btn-ghost btn-xs fishy-loot-condition-auto" type="button" data-rate-alternative-auto data-loot-condition-key="${escapeText(family.familyId)}" aria-label="${escapeText(t(
                "calculator.data.condition.automatic",
                "Use automatic condition",
            ))}" data-fishy-tooltip="${escapeText(t(
                "calculator.data.condition.automatic",
                "Use automatic condition",
            ))}"${modelLoading ? " disabled" : ""}>${escapeText(t(
                "calculator.data.condition.auto_short",
                "Auto",
            ))}</button>`
            : "")
        + "</div>"
    )).join("");
    return `<div class="fishymap-zone-loot-group-header" data-loot-editor-group-header data-loot-provenance-selected="${provenanceSelected}">`
        + '<div class="fishymap-zone-loot-group-heading"><div class="flex-wrap" data-loot-editor-group-title>'
        + assignmentControl
        + `<h4><button class="btn btn-xs ${groupSelectClasses} shadow-none hover:shadow-none focus:shadow-none active:shadow-none" type="button" aria-label="${escapeText(inspectGroupLabel)}" aria-pressed="${provenanceSelected}" data-loot-editor-group-select data-action="select-loot-group" data-group-id="${escapeText(groupId)}">${spriteIcon(groupSelectIcon, "size-3.5")}<span>${escapeText(label)}</span></button></h4>`
        + mainGroupBadge
        + `</div>${conditionSwitchers}</div>`
        + '<div class="fishymap-zone-loot-group-rate">'
        + renderLootMetricCell({
            primary: lootMetricValue(displayedRate, { compactNonKnown: true }),
            secondary: null,
            tone: lootMetricTone(groupPresentation),
            control: {
                type: "button",
                action: groupRateAction,
                ariaLabel: accessibleGroupRateLabel,
                groupId,
                expanded: editingGroupRate,
                disabled: modelLoading,
            },
        })
        + renderShadowedGroupBonusAlert(
            groupRate.rate,
            authoringGroupRates.shadowedBonuses,
            groupId,
        )
        + groupProvenance
        + (groupPresenceFact
            ? `<button class="btn btn-outline btn-xs ${groupIsActive ? "btn-error" : "btn-success"}" type="button" aria-label="${escapeText(groupPresenceAriaLabel)}" data-action="${groupPresenceAction}" data-group-id="${escapeText(groupId)}"${modelLoading ? " disabled" : ""}>${escapeText(groupPresenceLabel)}</button>`
            : "")
        + '</div>'
        + '</div>'
        + (canRenderGroupRateForm
            ? renderLootRateForm({
                scope: "group",
                groupId,
                rateFact: groupRate,
                projectedRates: displayedGroupRates,
                projectionDisplayMode: lootRateDisplayMode,
                projectionLocale: locale,
                projectionTone: lootMetricTone(groupPresentation),
                preferredBasis: rateEditor?.inputBasis,
                modelLoading,
            })
            : "")
        + `<ol class="fishymap-zone-loot-group-rows" data-loot-editor-group-list>${list}</ol>`;
}

function renderLootGroup(options) {
    const label = presentedGroupLabel(options.groupId, options.dataPresentation);
    const presence = effectiveGroupPresenceFact(options.traces, options.groupId)?.presence
        ?? projectedGroupRow(options.projection, options.groupId)?.presence
        ?? "unknown";
    return `<section class="fishymap-zone-loot-group rounded-box border border-base-300 bg-base-200/75 p-2" aria-label="${escapeText(t("calculator.data.group.label", "{ $group } group", { group: label }))}" data-loot-editor-group="${escapeText(options.groupId)}" data-group-presence-state="${escapeText(presence)}">`
        + renderLootGroupContent(options)
        + '</section>';
}

const CATALOG_SECTION_ORDER = Object.freeze([
    "custom",
    "assigned",
    "assignedAbsent",
    "available",
]);

function editorPresentationOrder(projection) {
    return Object.freeze({
        groups: new Map((projection.presentationOrder.groups ?? []).map((entry) => [
            entry.groupId,
            entry.rank,
        ])),
        items: new Map((projection.presentationOrder.items ?? []).map((entry) => [
            `${entry.groupId}\u0000${entry.itemId}`,
            entry.rank,
        ])),
    });
}

function projectionNumericSortValue(value) {
    if (value?.state === "known") {
        return { state: "known", value: value.value };
    }
    if (value?.state === "unknown" || value?.state === "unavailable") {
        return { state: value.state };
    }
    return { state: "unknown" };
}

function projectionRateSortValue(row) {
    if (row?.rate && !Object.prototype.hasOwnProperty.call(row.rate, "state")) {
        return { state: "known", value: row.rate.value };
    }
    if (row?.rate?.state === "known") {
        return { state: "known", value: row.rate.value };
    }
    if (row?.rate?.state === "unavailable") {
        return { state: "unavailable" };
    }
    return { state: "unknown" };
}

function databaseSortRank(order, groupIds, itemId) {
    let selected = Number.MAX_SAFE_INTEGER;
    for (const groupId of groupIds) {
        const groupRank = order.groups.get(groupId) ?? 1_000_000;
        const itemRank = order.items.get(`${groupId}\u0000${itemId}`) ?? 1_000_000;
        selected = Math.min(selected, groupRank * 1_000_001 + itemRank);
    }
    return selected;
}

function primaryProjectedItem(projection, order, groupIds, itemId) {
    return [...groupIds]
        .map((groupId) => ({
            groupId,
            row: projectedItemRow(projection, groupId, itemId),
            rank: databaseSortRank(order, [groupId], itemId),
        }))
        .filter(({ row }) => row)
        .sort((left, right) => left.rank - right.rank)[0]?.row ?? null;
}

function editorEvidenceSegments({
    provenanceResolver,
    traceIds,
    semanticMemberships,
    itemId,
    presentationOrder,
}) {
    if (!provenanceResolver) {
        return [];
    }
    const memberships = semanticMemberships.slice().sort((left, right) => (
        (presentationOrder.groups.get(left.groupId) ?? Number.MAX_SAFE_INTEGER)
            - (presentationOrder.groups.get(right.groupId) ?? Number.MAX_SAFE_INTEGER)
        || left.groupId.localeCompare(right.groupId)
        || left.traceIds.join("\u0000").localeCompare(right.traceIds.join("\u0000"))
    ));
    if (memberships.length) {
        return memberships.flatMap(({ groupId, traceIds: membershipTraceIds }, index) => (
            provenanceResolver.resolveInspectorItem(
                membershipTraceIds,
                itemId,
                groupId,
                `lootEditor.items.${itemId}.sortEvidence.memberships[${index}]`,
            )
        ));
    }
    return traceIds.length
        ? provenanceResolver.resolve(
            traceIds,
            `lootEditor.items.${itemId}.sortEvidence.presence`,
        ).filter(({ active }) => active)
        : [];
}

function editorItemSortValues({
    projection,
    itemId,
    groupIds,
    traceIds,
    semanticMemberships,
    customCatalogItems,
    dataPresentation,
    priceStateResolver,
    provenanceResolver,
    presentationOrder,
}) {
    const itemPresentation = dataPresentation?.itemsByItemId?.[String(itemId)]
        ?? dataPresentation?.unknownItem
        ?? FALLBACK_ITEM_STYLE;
    const projected = primaryProjectedItem(
        projection,
        presentationOrder,
        groupIds,
        itemId,
    );
    const evidenceSegments = editorEvidenceSegments({
        provenanceResolver,
        traceIds,
        semanticMemberships,
        itemId,
        presentationOrder,
    });
    return calculatorLootRowSortValues({
        price: priceStateResolver.resolve(
            itemId,
            `lootEditor.items.${itemId}.sortPrice`,
        ),
        grade: {
            state: "known",
            value: Number.isFinite(itemPresentation.gradeRank) ? itemPresentation.gradeRank : 0,
        },
        dropRate: projectionRateSortValue(projected),
        expectedValue: projectionNumericSortValue(projected?.expectedProfit),
        fishingExperience: projectionNumericSortValue(
            projected?.expectedFishingExperience,
        ),
        totemExperience: projectionNumericSortValue(projected?.expectedTotemExperience),
        evidence: calculatorLootEvidenceSortValue(evidenceSegments),
        database: {
            state: "known",
            value: databaseSortRank(presentationOrder, groupIds, itemId),
        },
        itemId: { state: "known", value: itemId },
        label: presentedItemLabel(itemId, customCatalogItems, dataPresentation),
    });
}

function catalogSection(entry) {
    if (entry.custom) {
        return "custom";
    }
    if (entry.groups.size > 0 || entry.unquantifiedGroups.size > 0) {
        return entry.presence?.state === "absent"
            ? "assignedAbsent"
            : "assigned";
    }
    return "available";
}

function itemPresenceSortRank(entry) {
    if (entry.presence?.state === "present") {
        return 0;
    }
    if (entry.presence?.state === "absent") {
        return 2;
    }
    return 1;
}

function compareCatalogEntries(left, right, lootSort) {
    return CATALOG_SECTION_ORDER.indexOf(left.catalogSection)
        - CATALOG_SECTION_ORDER.indexOf(right.catalogSection)
        || itemPresenceSortRank(left) - itemPresenceSortRank(right)
        || compareCalculatorLootRows(left, right, lootSort);
}

function compareGroupAssignments(left, right, lootSort) {
    return itemPresenceSortRank(left) - itemPresenceSortRank(right)
        || compareCalculatorLootRows(left, right, lootSort);
}

function collectAvailableItems({
    projection,
    fishingSource,
    memberships,
    presences,
    rankingEvidenceRows,
    userLayerOperations,
    customCatalogItems,
    dataPresentation,
    selectedItemId,
    priceStateResolver,
    provenanceResolver,
    lootSort,
}) {
    const entries = new Map();
    const presentationOrder = editorPresentationOrder(projection);
    const ensure = (itemId) => {
        const existing = entries.get(itemId);
        if (existing) {
            return existing;
        }
        const created = {
            itemId,
            ranking: null,
            custom: false,
            selectedOnly: false,
            userLayer: false,
            presence: null,
            unquantifiedGroups: new Set(),
            groups: new Map(),
        };
        entries.set(itemId, created);
        return created;
    };
    for (const row of rankingEvidenceRows) {
        ensure(row.itemId).ranking = row;
    }
    for (const [itemId, presence] of presences) {
        ensure(itemId).presence = presence;
    }
    for (const item of projection.unquantifiedItems ?? []) {
        if (typeof item.groupId === "string" && item.groupId) {
            ensure(item.itemId).unquantifiedGroups.add(item.groupId);
        } else {
            ensure(item.itemId);
        }
    }
    for (const operation of userLayerOperations) {
        if (
            calculatorFishingSourcesEqual(operation.fishingSource, fishingSource)
            && Number.isInteger(operation.itemId)
            && operation.itemId > 0
        ) {
            ensure(operation.itemId).userLayer = true;
        }
    }
    for (const item of customCatalogItems) {
        ensure(item.itemId).custom = true;
    }
    for (const membership of memberships) {
        ensure(membership.itemId).groups.set(membership.groupId, membership);
    }
    if (Number.isInteger(selectedItemId) && selectedItemId > 0) {
        ensure(selectedItemId).selectedOnly = true;
    }
    return [...entries.values()]
        .map((entry) => Object.freeze({
            ...entry,
            catalogSection: catalogSection(entry),
            sortValues: editorItemSortValues({
                projection,
                itemId: entry.itemId,
                groupIds: new Set([
                    ...entry.groups.keys(),
                    ...entry.unquantifiedGroups,
                ]),
                traceIds: uniqueTraceIds(
                    entry.presence?.traceId,
                    entry.ranking?.presenceTraceIds ?? [],
                    [...entry.groups.values()].flatMap((membership) => (
                        membership.projectionTraceIds
                    )),
                ),
                semanticMemberships: [...entry.groups.values()].map((membership) => ({
                    groupId: membership.groupId,
                    traceIds: membership.projectionTraceIds,
                })),
                customCatalogItems,
                dataPresentation,
                priceStateResolver,
                provenanceResolver,
                presentationOrder,
            }),
        }))
        .sort((left, right) => compareCatalogEntries(left, right, lootSort));
}

function renderAvailableItem(
    entry,
    projection,
    selectedItemId,
    itemFactsEditorItemId,
    customCatalogItems,
    dataPresentation,
    provenanceResolver,
    priceTextResolver,
    provenanceDetailBindings,
    modelLoading,
) {
    const identity = lootItemIdentity(
        entry.itemId,
        customCatalogItems,
        dataPresentation,
        priceTextResolver,
    );
    const selected = entry.itemId === selectedItemId;
    const presenceTraceIds = uniqueTraceIds(
        entry.presence?.traceId,
        entry.ranking?.presenceTraceIds ?? [],
    );
    const provenanceSegments = editorPresenceProvenanceSegments(
        provenanceResolver,
        entry.presence?.traceId ?? null,
        presenceTraceIds,
        entry.itemId,
        `lootEditor.catalog.${entry.itemId}`,
    );
    const selectLabel = t(
        "calculator.data.action.select_item",
        "Select { $item }",
        { item: identity.label },
    );
    const removeCustomLabel = t(
        "calculator.data.action.remove_custom_item",
        "Remove { $item } from Custom",
        { item: identity.label },
    );
    const removeControl = entry.custom
        ? `<button class="btn btn-ghost btn-square btn-sm text-error" type="button" aria-label="${escapeText(removeCustomLabel)}" data-fishy-tooltip="${escapeText(removeCustomLabel)}" data-fishy-tooltip-mode="anchor-only" data-action="remove-custom-catalog-item" data-item-id="${entry.itemId}">${spriteIcon("trash")}</button>`
        : "";
    const factsControls = renderCalculatorCatalogItemFactsControls({
        state: createCalculatorCatalogItemFactsEditorState({
            projection,
            itemId: entry.itemId,
            openItemId: itemFactsEditorItemId,
        }),
        itemLabel: identity.label,
        modelLoading,
    });
    const presenceState = entry.presence?.state ?? "unknown";
    const assignedAbsent = presenceState === "absent"
        && (entry.groups.size > 0 || entry.unquantifiedGroups.size > 0);
    const presenceBadge = assignedAbsent
        ? `<span class="badge badge-soft badge-error badge-sm whitespace-nowrap">${escapeText(t(
            "calculator.data.presence.label",
            "Presence: { $state }",
            { state: enumLabel("absent") },
        ))}</span>`
        : "";
    return `<li class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1" data-loot-editor-available-item="${entry.itemId}" data-item-id="${entry.itemId}" data-selected="${selected}"${assignedAbsent ? ' data-presence-state="absent"' : ""}${selected ? " data-loot-editor-selected-source" : ""}>`
        + lootItemRowMarkup({
            rowId: `catalog:${entry.itemId}`,
            item: lootItemPresentation(identity, {
                type: "button",
                action: "select-data-item",
                ariaLabel: selectLabel,
                itemId: entry.itemId,
                pressed: selected,
            }, dataPresentation),
            leadingMetric: null,
            trailingMetric: null,
            provenanceSegments,
            provenanceLabel: dataPresentation?.labels?.provenanceLabel
                ?? t("common.provenance.label.default", "Provenance"),
        }, {
            provenanceDetailBindings,
        })
        + `<div class="relative z-10 flex flex-wrap items-center justify-end gap-1" data-loot-editor-catalog-actions>${presenceBadge}${factsControls.toggle}${removeControl}</div>`
        + factsControls.panel
        + '</li>';
}

function catalogSectionLabel(section) {
    if (section === "custom") {
        return t("calculator.data.catalog.section.custom", "Custom");
    }
    if (section === "assigned") {
        return t("calculator.data.catalog.section.assigned", "Assigned");
    }
    if (section === "assignedAbsent") {
        return t(
            "calculator.data.catalog.section.assigned_absent",
            "Assigned · absent",
        );
    }
    return t("calculator.data.catalog.section.available", "Available");
}

function renderAvailableCatalogMarkup(
    entries,
    allEntries,
    projection,
    selectedItemId,
    itemFactsEditorItemId,
    customCatalogItems,
    dataPresentation,
    provenanceResolver,
    priceTextResolver,
    provenanceDetailBindings,
    modelLoading,
) {
    if (!entries.length) {
        return `<li class="text-sm opacity-60">${escapeText(t("calculator.data.available.none", "No items are available for this fishing source."))}</li>`;
    }
    const counts = new Map(CATALOG_SECTION_ORDER.map((section) => [
        section,
        allEntries.filter((entry) => entry.catalogSection === section).length,
    ]));
    let previousSection = null;
    return entries.map((entry) => {
        const sectionHeading = entry.catalogSection === previousSection
            ? ""
            : `<li class="flex items-center gap-2 px-1 pt-2 text-xs font-semibold" data-loot-editor-catalog-section="${escapeText(entry.catalogSection)}"><span>${escapeText(catalogSectionLabel(entry.catalogSection))}</span><span class="badge badge-ghost badge-xs">${counts.get(entry.catalogSection) ?? 0}</span><span class="h-px grow bg-base-300" aria-hidden="true"></span></li>`;
        previousSection = entry.catalogSection;
        return sectionHeading + renderAvailableItem(
            entry,
            projection,
            selectedItemId,
            itemFactsEditorItemId,
            customCatalogItems,
            dataPresentation,
            provenanceResolver,
            priceTextResolver,
            provenanceDetailBindings,
            modelLoading,
        );
    }).join("");
}

const calculatorLootTableEditorRenderStates = new WeakSet();

function calculatorLootTableEditorRenderState(value) {
    if (!calculatorLootTableEditorRenderStates.has(value)) {
        throw new TypeError(
            "calculator loot-table editor render state must come from createCalculatorLootTableEditorRenderState()",
        );
    }
    return value;
}

/**
 * Resolves the editor's typed projection once for both full and regional paints.
 * Fragment renderers below are the only owners of catalog rows and group rows.
 */
export function createCalculatorLootTableEditorRenderState({
    projection,
    fishingSource,
    groupIds,
    customCatalogItems = [],
    catalogSearchUrl = "",
    dataPresentation,
    lootSort = DEFAULT_CALCULATOR_LOOT_SORT,
    lootRateDisplayMode = "normalized",
    selectedItemId,
    itemFactsEditorItemId = null,
    selectedLootSubject = null,
    modelLoading = false,
    rateEditor,
    rankingEvidenceRows = [],
    userLayerOperations = [],
}) {
    const parsedLootSort = parseCalculatorLootSort(lootSort);
    const parsedLootRateDisplayMode = parseCalculatorLootRateDisplayMode(
        lootRateDisplayMode,
        "lootRateDisplayMode",
    );
    const parsedLootSubject = parseCalculatorLootSubject(
        selectedLootSubject,
        "selectedLootSubject",
    );
    const traces = calculatorFishingSourceTraces(projection, fishingSource);
    const presences = effectiveItemPresences(traces);
    const memberships = effectiveMemberships(
        projection,
        traces,
        presences,
    );
    const provenanceResolver = dataPresentation
        ? createCalculatorProvenanceSegmentResolver(projection, dataPresentation)
        : null;
    const priceTextResolver = dataPresentation
        ? createCalculatorItemPriceTextResolver(projection, dataPresentation)
        : null;
    const priceStateResolver = createCalculatorItemPriceStateResolver(projection);
    const available = collectAvailableItems({
        projection,
        fishingSource,
        memberships,
        presences,
        rankingEvidenceRows,
        userLayerOperations,
        customCatalogItems,
        dataPresentation,
        selectedItemId,
        priceStateResolver,
        provenanceResolver,
        lootSort: parsedLootSort,
    });
    const selectedEntry = available.find((entry) => entry.itemId === selectedItemId) ?? null;
    const visibleAvailable = available.slice(0, 60);
    if (selectedEntry && !visibleAvailable.includes(selectedEntry)) {
        visibleAvailable.push(selectedEntry);
    }
    visibleAvailable.sort((left, right) => compareCatalogEntries(
        left,
        right,
        parsedLootSort,
    ));
    const presentationOrder = editorPresentationOrder(projection);
    const assignmentsByGroup = new Map(groupIds.map((groupId) => [
        groupId,
        memberships
            .filter((entry) => entry.groupId === groupId)
            .map((entry) => Object.freeze({
                ...entry,
                sortValues: editorItemSortValues({
                    projection,
                    itemId: entry.itemId,
                    groupIds: new Set([entry.groupId]),
                    traceIds: entry.projectionTraceIds,
                    semanticMemberships: [{
                        groupId: entry.groupId,
                        traceIds: entry.projectionTraceIds,
                    }],
                    customCatalogItems,
                    dataPresentation,
                    priceStateResolver,
                    provenanceResolver,
                    presentationOrder,
                }),
            }))
            .sort((left, right) => compareGroupAssignments(left, right, parsedLootSort)),
    ]));
    const selectedConnections = groupIds.flatMap((groupId) => (
        assignmentsByGroup.get(groupId) ?? []
    )).filter((entry) => entry.itemId === selectedItemId);
    const itemCount = t(
        available.length === 1
            ? "calculator.data.available.item_count.one"
            : "calculator.data.available.item_count.other",
        available.length === 1 ? "{ $count } item" : "{ $count } items",
        { count: available.length },
    );
    const showing = available.length > visibleAvailable.length
        ? t("calculator.data.available.showing", " · showing { $count }", {
            count: visibleAvailable.length,
        })
        : "";
    const selectedConnectionColor = selectedEntry
        ? lootItemIdentity(
            selectedEntry.itemId,
            customCatalogItems,
            dataPresentation,
            priceTextResolver,
        ).connectorColor
        : FALLBACK_ITEM_STYLE.connectorColor;
    const state = Object.freeze({
        projection,
        fishingSource,
        groupIds: Object.freeze([...groupIds]),
        customCatalogItems,
        catalogSearchUrl,
        dataPresentation,
        lootSort: parsedLootSort,
        lootRateDisplayMode: parsedLootRateDisplayMode,
        selectedItemId,
        itemFactsEditorItemId,
        selectedLootSubject: parsedLootSubject,
        modelLoading,
        rateEditor,
        traces,
        provenanceResolver,
        priceTextResolver,
        available,
        visibleAvailable,
        assignmentsByGroup,
        rateAlternativeFamiliesByGroup: new Map(groupIds.map((groupId) => [
            groupId,
            calculatorRateAlternativeFamiliesForGroup({
                projection,
                fishingSource,
                groupId,
            }),
        ])),
        availableSummary: itemCount + showing,
        selectedConnectionColor,
        hasSelectedConnections: selectedConnections.length > 0,
    });
    calculatorLootTableEditorRenderStates.add(state);
    return state;
}

export function renderCalculatorLootTableEditorAvailableList(
    value,
    { provenanceDetailBindings = null } = {},
) {
    const state = calculatorLootTableEditorRenderState(value);
    return renderAvailableCatalogMarkup(
        state.visibleAvailable,
        state.available,
        state.projection,
        state.selectedItemId,
        state.itemFactsEditorItemId,
        state.customCatalogItems,
        state.dataPresentation,
        state.provenanceResolver,
        state.priceTextResolver,
        provenanceDetailBindings,
        state.modelLoading,
    );
}

function lootGroupRenderOptions(state, groupId, provenanceDetailBindings) {
    if (!state.groupIds.includes(groupId)) {
        throw new TypeError(`calculator loot-table editor has no group ${groupId}`);
    }
    return {
        groupId,
        assignments: state.assignmentsByGroup.get(groupId) ?? [],
        selectedItemId: state.selectedItemId,
        selectedLootSubject: state.selectedLootSubject,
        customCatalogItems: state.customCatalogItems,
        dataPresentation: state.dataPresentation,
        provenanceResolver: state.provenanceResolver,
        priceTextResolver: state.priceTextResolver,
        projection: state.projection,
        traces: state.traces,
        rateEditor: state.rateEditor,
        modelLoading: state.modelLoading,
        lootRateDisplayMode: state.lootRateDisplayMode,
        provenanceDetailBindings,
        rateAlternativeFamilies: state.rateAlternativeFamiliesByGroup.get(groupId) ?? [],
    };
}

export function renderCalculatorLootTableEditorGroupContent(
    value,
    groupId,
    { provenanceDetailBindings = null } = {},
) {
    const state = calculatorLootTableEditorRenderState(value);
    return renderLootGroupContent(
        lootGroupRenderOptions(state, groupId, provenanceDetailBindings),
    );
}

export function renderCalculatorLootTableEditorGroup(
    value,
    groupId,
    { provenanceDetailBindings = null } = {},
) {
    const state = calculatorLootTableEditorRenderState(value);
    return renderLootGroup(
        lootGroupRenderOptions(state, groupId, provenanceDetailBindings),
    );
}

export function renderCalculatorLootTableEditorMarkup({
    provenanceDetailBindings = null,
    ...input
}) {
    const state = createCalculatorLootTableEditorRenderState(input);
    return renderCalculatorLootTableEditorFromState(state, {
        provenanceDetailBindings,
    });
}

function renderCatalogPicker(state) {
    const label = t("calculator.data.catalog.add_item", "Add catalog item");
    const placeholder = t(
        "calculator.data.search.catalog_placeholder",
        "Search full catalog",
    );
    const excludedInputs = state.customCatalogItems.map((item) => (
        `<input type="hidden" value="${item.itemId}" data-loot-editor-custom-item-id>`
    )).join("");
    return `<fishy-searchable-dropdown id="calculator-loot-item-picker" class="relative block w-full" label="${escapeText(label)}" value="" search-url="${escapeText(state.catalogSearchUrl)}" exclude-selected-inputs="[data-loot-editor-custom-item-id]" placeholder="${escapeText(placeholder)}" panel-mode="detached" panel-placement="overlay-anchor" panel-min-width="panel" panel-width="24rem" data-loot-editor-catalog-picker>`
        + excludedInputs
        + '<button class="flex min-h-10 w-full items-center justify-between gap-3 rounded-box border border-base-300 bg-base-100 px-3 py-2 text-left text-sm shadow-sm" type="button" data-role="trigger" aria-haspopup="listbox" aria-expanded="false" aria-controls="calculator-loot-item-picker-panel">'
        + `<span data-role="selected-content">${escapeText(label)}</span>${spriteIcon("caret-down")}</button>`
        + '<div id="calculator-loot-item-picker-panel" class="absolute left-0 top-0 z-50 w-full min-w-full max-w-full" data-role="panel" hidden>'
        + '<div class="grid w-full min-w-full overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg">'
        + '<label class="flex min-h-10 w-full min-w-full items-center gap-3 bg-base-100 px-3 py-2 text-sm">'
        + spriteIcon("search-field", "size-4 opacity-60")
        + `<input id="calculator-loot-item-picker-search" class="w-full border-0 bg-transparent p-0 shadow-none outline-none" style="outline: none; box-shadow: none;" type="search" data-role="search-input" placeholder="${escapeText(placeholder)}" autocomplete="off" spellcheck="false">`
        + '</label>'
        + '<div class="max-h-80 overflow-y-auto px-1 pb-1">'
        + '<ul id="calculator-loot-item-picker-results" class="menu menu-sm w-full" data-role="results" role="listbox"></ul>'
        + '</div></div></div></fishy-searchable-dropdown>';
}

export function renderCalculatorLootTableEditorFromState(
    value,
    { provenanceDetailBindings = null } = {},
) {
    const state = calculatorLootTableEditorRenderState(value);
    const availableMarkup = renderCalculatorLootTableEditorAvailableList(state, {
        provenanceDetailBindings,
    });
    const groupMarkup = state.groupIds.length
        ? state.groupIds.map((groupId) => renderCalculatorLootTableEditorGroup(
            state,
            groupId,
            { provenanceDetailBindings },
        )).join("")
        : `<div class="alert"><span>${escapeText(t("calculator.data.group.none_available", "No groups are available for this selection."))}</span></div>`;
    return `<section class="card border border-primary/30 bg-base-100" aria-labelledby="calculator-loot-editor-heading" aria-busy="${state.modelLoading}" data-loot-editor>`
        + '<div class="card-body gap-4 p-4">'
        + `<div><h3 class="card-title text-base" id="calculator-loot-editor-heading">${escapeText(t("calculator.data.editor.title", "Loot table editor"))}</h3>`
        + `<p class="text-sm opacity-70">${escapeText(t("calculator.data.editor.description", "Select an item, then use a group arrow to assign it. New items begin present with an unknown rate; known rates enter calculations immediately."))}</p></div>`
        + `<fishy-calculator-assignment-connections class="grid gap-4" data-loot-editor-workspace data-loot-editor-has-connections="${state.hasSelectedConnections}" style="--loot-editor-connection-color:${escapeText(state.selectedConnectionColor)};">`
        + '<aside class="!z-10 grid content-start gap-3" data-loot-editor-available aria-labelledby="calculator-loot-editor-available-heading">'
        + `<div><h4 class="font-semibold" id="calculator-loot-editor-available-heading">${escapeText(t("calculator.data.catalog.title", "Catalog"))}</h4>`
        + `<p class="text-xs opacity-60" data-loot-editor-available-summary>${escapeText(state.availableSummary)}</p></div>`
        + '<div data-loot-editor-search>'
        + renderCatalogPicker(state)
        + '</div>'
        + `<ul class="list" data-loot-editor-available-list>${availableMarkup}</ul>`
        + '</aside>'
        + '<span data-loot-editor-connection-lane aria-hidden="true"></span>'
        + `<div class="grid content-start gap-3" data-loot-editor-targets><div class="fishymap-zone-loot-groups" data-loot-editor-groups>${groupMarkup}</div></div>`
        + '</fishy-calculator-assignment-connections></div></section>';
}
